import { Helmet } from 'react-helmet-async';
import LegalPage from '../components/LegalPage';
import { PHONE, PHONE_HREF } from '../contact.config';

const sections = [
  {
    heading: 'Introduction & Our Commitment',
    content: [
      'YN Dream Curtains ("we", "us", "our") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>yndreamcurtains.com</strong> or make use of our services in Adelaide, South Australia.',
      'We take your privacy seriously. If you do not agree with the terms of this Privacy Policy, please discontinue use of our site. By accessing our website or booking our services, you acknowledge that you have read and understood this policy.',
      'YN Dream Curtains operates in compliance with the <strong>Australian Privacy Act 1988 (Cth)</strong> and the <strong>Australian Privacy Principles (APPs)</strong>.',
    ],
  },
  {
    heading: 'Information We Collect',
    content: [
      'We collect information that you voluntarily provide to us when you use our contact form, book a consultation online, request a quote, or otherwise communicate with us. The types of personal information we may collect include:',
      '• <strong>Identity Information:</strong> Full name, as provided when booking or contacting us.',
      '• <strong>Contact Information:</strong> Email address, phone number, and residential address (for in-home consultation bookings).',
      '• <strong>Service Preferences:</strong> Information about your property, rooms, and the window furnishing products you are interested in.',
      '• <strong>Communication Records:</strong> Records of correspondence and enquiries you send us.',
      '• <strong>Technical Data:</strong> IP address, browser type, pages visited, and time spent on our website (collected automatically via cookies and analytics tools).',
      '• <strong>Marketing Preferences:</strong> Whether you have opted in to receive newsletters or promotional communications from us.',
      'We do not collect sensitive personal information such as financial account details, government identifiers, or health information unless strictly required and with your explicit consent.',
    ],
  },
  {
    heading: 'How We Use Your Information',
    content: [
      'We use the personal information we collect for the following lawful purposes:',
      '• To process your consultation booking and arrange an in-home visit by our specialists.',
      '• To provide you with accurate, personalised quotes for window furnishing products and installation services.',
      '• To communicate with you about your enquiry, order, or appointment — including confirmation emails and follow-ups.',
      '• To improve our website, services, and customer experience through analysis of usage data.',
      '• To send you marketing communications about our products, promotions, or news (only where you have opted in).',
      '• To comply with legal obligations and resolve any disputes that may arise.',
      '• To protect the security and integrity of our business and website.',
    ],
  },
  {
    heading: 'Sharing Your Information',
    content: [
      'YN Dream Curtains does not sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:',
      '• <strong>Service Providers:</strong> Trusted third-party service providers who assist us in operating our website or delivering our services (e.g. email delivery platforms, scheduling software). These parties are bound by confidentiality obligations.',
      '• <strong>Legal Obligations:</strong> Where we are required by law, court order, or government authority to disclose your information.',
      '• <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of business assets, your information may be transferred to the new entity, subject to equivalent privacy protections.',
      'All third parties we work with are required to handle your data securely and in accordance with applicable privacy laws.',
    ],
  },
  {
    heading: 'Cookies & Tracking Technologies',
    content: [
      'Our website uses cookies and similar tracking technologies to enhance your browsing experience and gather analytical data. Cookies are small text files stored on your device when you visit our website.',
      '• <strong>Essential Cookies:</strong> Required for the website to function correctly. These cannot be disabled.',
      '• <strong>Analytics Cookies:</strong> Used to understand how visitors interact with our website (e.g. Google Analytics). All data is anonymised and aggregated.',
      '• <strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and measure the effectiveness of marketing campaigns.',
      'You can control or disable cookies through your browser settings at any time. Please note that disabling certain cookies may affect the functionality of our website.',
    ],
  },
  {
    heading: 'Data Retention',
    content: [
      'We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy, or as required by applicable law. Specifically:',
      '• Customer records and consultation data are retained for up to <strong>7 years</strong> in compliance with Australian tax and business record-keeping obligations.',
      '• Marketing preference data is retained until you withdraw consent or request deletion.',
      '• Website analytics data is typically retained for <strong>26 months</strong> before being anonymised or deleted.',
      'When personal information is no longer required, we securely destroy or de-identify it.',
    ],
  },
  {
    heading: 'Your Rights & Access to Information',
    content: [
      'Under the Australian Privacy Act 1988 and applicable privacy principles, you have the right to:',
      '• <strong>Access:</strong> Request a copy of the personal information we hold about you.',
      '• <strong>Correction:</strong> Request that we correct any inaccurate or incomplete information.',
      '• <strong>Deletion:</strong> Request deletion of your personal data, subject to legal retention obligations.',
      '• <strong>Opt-Out:</strong> Withdraw consent for marketing communications at any time by clicking "unsubscribe" in any email or contacting us directly.',
      '• <strong>Complaint:</strong> Lodge a complaint with the <strong>Office of the Australian Information Commissioner (OAIC)</strong> at oaic.gov.au if you believe we have mishandled your information.',
      'To exercise any of these rights, please contact us via our <a href="/contact">Contact page</a>. We will respond to all requests within 30 days.',
    ],
  },
  {
    heading: 'Security of Your Information',
    content: [
      'We implement appropriate technical and organisational security measures to protect your personal information from unauthorised access, disclosure, alteration, or destruction. These measures include encrypted data transmission (HTTPS), secure server infrastructure, and restricted staff access to personal data.',
      'However, no method of internet transmission or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security. We encourage you to use strong passwords and to be cautious when sharing personal information online.',
    ],
  },
  {
    heading: 'Links to Third-Party Websites',
    content: [
      'Our website may contain links to third-party websites (e.g. our Facebook, Instagram, or TikTok pages). These external sites have their own privacy policies, which we encourage you to review. YN Dream Curtains is not responsible for the privacy practices or content of third-party sites.',
    ],
  },
  {
    heading: 'Children\'s Privacy',
    content: [
      'Our services are intended for adults aged 18 and over. We do not knowingly collect personal information from children under the age of 18. If you believe a child has provided us with personal data, please contact us immediately and we will promptly delete such information.',
    ],
  },
  {
    heading: 'Changes to This Policy',
    content: [
      'YN Dream Curtains reserves the right to update or modify this Privacy Policy at any time. Any changes will be published on this page with an updated "Last Updated" date. We encourage you to review this policy periodically. Continued use of our website after any changes constitutes acceptance of the updated policy.',
    ],
  },
  {
    heading: 'Contact & Complaints',
    content: [
      'If you have any questions, concerns, or complaints regarding this Privacy Policy or how we handle your personal information, please contact us:',
      `<strong>YN Dream Curtains</strong><br/>Adelaide SA, Australia<br/>Phone: <a href="${PHONE_HREF}">${PHONE}</a><br/>Website: <a href="https://yndreamcurtains.com" target="_blank" rel="noopener noreferrer">yndreamcurtains.com</a>`,
      'We take all privacy concerns seriously and will respond to your enquiry within 30 days. If you are not satisfied with our response, you may escalate your complaint to the Office of the Australian Information Commissioner at <strong>oaic.gov.au</strong>.',
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | YN Dream Curtains Adelaide</title>
        <meta name="description" content="YN Dream Curtains Privacy Policy — how we collect, use and protect your personal information in accordance with Australian privacy law." />
        <link rel="canonical" href="https://yndreamcurtains.com/privacy-policy" />
      </Helmet>
      <LegalPage
        title="Privacy Policy"
        subtitle="How YN Dream Curtains collects, uses, and protects your personal information — in compliance with the Australian Privacy Act 1988."
        lastUpdated="January 2025"
        sections={sections}
        relatedLinks={[
          { label: 'Terms & Conditions', to: '/terms' },
          { label: 'Refund Policy', to: '/refund-policy' },
          { label: 'Accessibility Statement', to: '/accessibility' },
        ]}
      />
    </>
  );
}
